// Lectura de una promo para editarla, y guardar: crear sin id, editar (versión nueva) con id.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shouldRetryPromotionsQuery } from '../lib/promotionsFormat';
import { isPromotionsConfigured, promotionsClient } from './promotions.client';
import type {
    CreateSalePromotionRequest,
    SalePromotion,
    UpdateSalePromotionRequest,
} from './promotionsContract';
import { PROMOTIONS_QUERY_KEY } from './usePromotions';

export function useSalePromotion(promotionId: string | null) {
    return useQuery({
        queryKey: [...PROMOTIONS_QUERY_KEY, 'detail', promotionId],
        queryFn: () => promotionsClient.getSalePromotion(promotionId ?? ''),
        enabled: promotionId !== null && isPromotionsConfigured(),
        retry: shouldRetryPromotionsQuery,
        // Se edita sobre la versión vigente, nunca sobre una en caché.
        staleTime: 0,
    });
}

export interface SaveSalePromotionInput {
    promotionId?: string;
    request: CreateSalePromotionRequest;
    retailerId?: string;
}

export function useSaveSalePromotion() {
    const qc = useQueryClient();
    return useMutation<SalePromotion, unknown, SaveSalePromotionInput>({
        mutationFn: ({ promotionId, request, retailerId }) => {
            const override = retailerId ? [{ retailerId }] : [];
            return promotionId === undefined
                ? promotionsClient.createSalePromotion(request, ...override)
                : promotionsClient.updateSalePromotion(promotionId, request, ...override);
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: PROMOTIONS_QUERY_KEY });
        },
    });
}

export interface UpdateSystemPromotionInput {
    promotionId: string;
    request: UpdateSalePromotionRequest;
    retailerId?: string;
}

/** Parche de una promo de sistema: el back solo admite el monto del bono. */
export function useUpdateSystemPromotion() {
    const qc = useQueryClient();
    return useMutation<SalePromotion, unknown, UpdateSystemPromotionInput>({
        mutationFn: ({ promotionId, request, retailerId }) =>
            retailerId
                ? promotionsClient.updateSalePromotion(promotionId, request, { retailerId })
                : promotionsClient.updateSalePromotion(promotionId, request),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: PROMOTIONS_QUERY_KEY });
        },
    });
}
